import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { RegisterForm, type RegisterFormProps, type RegisterFormValues } from "./RegisterForm";
import { EmailConfirmationForm, type EmailConfirmationFormProps } from "./EmailConfirmationForm";
import { AuthHttpError } from "../types";

export interface RegisterPageProps
  extends Pick<RegisterFormProps, "usernameLabel" | "emailLabel" | "minPasswordLength" | "showConfirmPassword">,
    Pick<EmailConfirmationFormProps, "codeLabel"> {
  title?: string;
  subtitle?: string;
  confirmationTitle?: string;
  confirmationSubtitle?: string;
  /** Called once the account is created and (unless `skipEmailConfirmation`) the email is confirmed. */
  onSuccess?: () => void;
  /** Rendered as a "Already have an account? Sign in" link under the registration form when provided. */
  onSwitchToLogin?: () => void;
  containerClassName?: string;
  /** Skip the confirmation-code step entirely and call `onSuccess` right after registration. Default false. */
  skipEmailConfirmation?: boolean;
}

function friendlyRegisterError(err: unknown): string {
  if (err instanceof AuthHttpError) {
    if (err.status === 409 || err.status === 400) return "That username or email is already registered.";
    if (err.status === 429) return "Too many attempts. Try again shortly.";
    if (err.code === "REGISTRATION_NO_SESSION") return "That username or email is already registered.";
    if (err.code === "NETWORK_ERROR") return "Can't reach the server. Check your connection.";
    return err.message || "Registration failed. Please try again.";
  }
  return "Registration failed. Please try again.";
}

function friendlyConfirmError(err: unknown): string {
  if (err instanceof AuthHttpError) {
    if (err.status === 401 || err.status === 400) return "That code is incorrect or has expired.";
    if (err.status === 429) return "Too many attempts. Try again shortly.";
    if (err.code === "NETWORK_ERROR") return "Can't reach the server. Check your connection.";
    return err.message || "Couldn't confirm your email. Please try again.";
  }
  return "Couldn't confirm your email. Please try again.";
}

/**
 * Ready-to-route registration page: collects username/email/password, then
 * — unless `skipEmailConfirmation` — walks the user through entering the
 * confirmation code sent to their address. Just a composition of `useAuth()`
 * + `RegisterForm` + `EmailConfirmationForm`; swap it entirely with your own
 * if you need different steps or layout.
 *
 * Only rendered meaningfully when the configured `AuthProvider` supports
 * registration (e.g. `JwtProvider`) — redirect-flow providers don't.
 */
export function RegisterPage({
  title = "Create your account",
  subtitle,
  confirmationTitle = "Confirm your email",
  confirmationSubtitle,
  onSuccess,
  onSwitchToLogin,
  containerClassName,
  skipEmailConfirmation = false,
  usernameLabel,
  emailLabel,
  minPasswordLength,
  showConfirmPassword,
  codeLabel,
}: RegisterPageProps) {
  const auth = useAuth();
  const [step, setStep] = useState<"register" | "confirm">("register");
  const [email, setEmail] = useState("");
  const [nonce, setNonce] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unsupported = !auth.register || !auth.requestEmailConfirmation || !auth.confirmEmailConfirmation;

  const handleRegister = async (values: RegisterFormValues) => {
    if (unsupported) return;
    setLoading(true);
    setError(null);
    try {
      await auth.register!({ username: values.username, password: values.password });

      if (skipEmailConfirmation) {
        onSuccess?.();
        return;
      }

      const handle = await auth.requestEmailConfirmation!(values.email);
      setEmail(values.email);
      setNonce(handle.nonce);
      setStep("confirm");
    } catch (err) {
      setError(friendlyRegisterError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (code: string) => {
    if (unsupported || !nonce) return;
    setLoading(true);
    setError(null);
    try {
      await auth.confirmEmailConfirmation!(nonce, code);
      onSuccess?.();
    } catch (err) {
      setError(friendlyConfirmError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (unsupported || !email) return;
    setResending(true);
    setError(null);
    try {
      const handle = await auth.requestEmailConfirmation!(email);
      setNonce(handle.nonce);
    } catch (err) {
      setError(friendlyConfirmError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className={
        containerClassName ??
        "fka-register-page flex min-h-screen items-center justify-center bg-slate-950 px-4"
      }
    >
      <div className="fka-register-card w-full max-w-sm rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        {unsupported ? (
          <p role="alert" className="fka-server-error text-sm text-red-400">
            The configured auth provider does not support registration.
          </p>
        ) : step === "register" ? (
          <>
            <h1 className="fka-register-title mb-1 text-xl font-semibold text-slate-100">{title}</h1>
            {subtitle && <p className="fka-register-subtitle mb-6 text-sm text-slate-400">{subtitle}</p>}
            <RegisterForm
              onSubmit={handleRegister}
              loading={loading}
              error={error}
              usernameLabel={usernameLabel}
              emailLabel={emailLabel}
              minPasswordLength={minPasswordLength}
              showConfirmPassword={showConfirmPassword}
            />
            {onSwitchToLogin && (
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="fka-switch-to-login mt-4 text-sm font-medium text-slate-400 underline-offset-2 hover:text-amber-400 hover:underline"
              >
                Already have an account? Sign in
              </button>
            )}
          </>
        ) : (
          <>
            <h1 className="fka-register-title mb-1 text-xl font-semibold text-slate-100">{confirmationTitle}</h1>
            {confirmationSubtitle && (
              <p className="fka-register-subtitle mb-6 text-sm text-slate-400">{confirmationSubtitle}</p>
            )}
            <EmailConfirmationForm
              onSubmit={handleConfirm}
              onResend={handleResend}
              loading={loading}
              resending={resending}
              error={error}
              email={email}
              codeLabel={codeLabel}
            />
          </>
        )}
      </div>
    </div>
  );
}
